import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { PlatformService } from "@obg/ngx.global";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { interval, merge, of, timer } from "rxjs";
import {
	catchError,
	delay,
	exhaustMap,
	filter,
	map,
	mergeMap,
	retryWhen,
	share,
	switchMap,
	take,
	takeUntil,
	takeWhile,
	tap,
	timeoutWith,
} from "rxjs/operators";

import { AppError } from "../app-error/app-error.model";
import { AUTH_MESSAGE_CODES } from "../auth/auth.const";
import { SESSION_ACTION_TYPE, SessionAction } from "../auth/session.action";
import { CancelVerificationData, CustomerNotVerifiedData } from "../auth/session.model";
import { SessionSelector } from "../auth/session.selector";
import { AuthTransactionSelector } from "../auth-transaction/auth-transaction.selector";
import { MessageScopeType, MessageType } from "../message/message.model";
import { MessageService } from "../message/message.service";
import { normalizePersonalNumber } from "../personal-number/personal-number.util";
import { CoreState } from "../state";

import { AuthTransactionTimingsService } from "./auth-transaction-timings.service";
import { AUTH_TRANSACTION_ACTION_TYPE, AuthTransactionAction } from "./auth-transaction.action";
import { AuthTransactionConfigService } from "./auth-transaction.config";
import { AUTH_TRANSACTION_CODES, AUTH_TRANSACTION_MESSAGE_CONTEXT } from "./auth-transaction.const";
import {
	AuthenticationType,
	HintCode,
	InitializeSuccessPayload,
	VerificationCancelRequest,
	VerificationRequest,
	VerificationStatus,
	VerificationStatusData,
	VerificationStatusResponse,
} from "./auth-transaction.model";
import { AuthTransactionService } from "./auth-transaction.service";

@Injectable()
export class AuthTransactionEffect {
	loginFailedWithPersonalNumber$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithPersonalNumberFail),
			map((x: ActionWithPayload) => x.payload),
			filter(
				(payload) =>
					payload.code === AUTH_MESSAGE_CODES.login.customerNotVerified &&
					payload.data.customerNotVerifiedLoginResponse
			),
			map((payload) => payload.data.customerNotVerifiedLoginResponse as CustomerNotVerifiedData),
			filter((data) => !!data && !!data.personalNumber && !!data.transactionId),
			map((data) =>
				this.action.getVerificationStatus({
					token: data.token!,
					personalNumber: data.personalNumber!,
					transactionId: data.transactionId!,
				})
			)
		)
	);

	loginFail$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail),
			map((x: ActionWithPayload) => x.payload),
			filter(
				(payload) =>
					payload.code === AUTH_MESSAGE_CODES.login.customerNotVerified &&
					payload.data.customerNotVerifiedLoginResponse
			),
			map((payload) => payload.data.customerNotVerifiedLoginResponse as CustomerNotVerifiedData),
			filter((data) => !!data && !!data.personalNumber),
			map((data) =>
				this.action.initializeVerification({ token: data.token, personalNumber: data.personalNumber! })
			)
		)
	);

	initializeAuthTransaction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction),
			map((x: ActionWithPayload) =>
				x.payload?.personalNumber
					? { ...x.payload, personalNumber: normalizePersonalNumber(x.payload.personalNumber) }
					: x.payload
			),
			concatLatestFrom(() => this.store.select(this.authTransactionSelector.getTransactionId())),
			switchMap(([payload, previousTransactionId]) =>
				this.service.initializeAuthTransaction(payload).pipe(
					retryWhen((errors) =>
						errors.pipe(
							mergeMap((error, index) => {
								if (error.code === AUTH_TRANSACTION_CODES.authenticationError && index === 0) {
									return timer(0);
								}
								throw error;
							})
						)
					),
					map((response) => {
						if (payload.startBankIdTimings) {
							this.timingsService.countdownInterval$.next(1000);
						}
						return this.action.initializeAuthTransactionSuccess({
							...payload,
							autostartToken: response.autostartToken,
							transactionId: response.transactionId,
							qrCode: response.qrCode,
							previousTransactionId,
						});
					}),
					catchError((error: AppError) =>
						(error.code === AUTH_TRANSACTION_CODES.validationError ||
							error.code === AUTH_TRANSACTION_CODES.authenticationError) &&
						error.data.errors &&
						error.data.errors.length
							? of(this.action.initializeAuthTransactionFail(new Error(error.data.errors[0].code)))
							: of(this.initializeFail(AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail, error))
					)
				)
			)
		)
	);

	initializeVerification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_TRANSACTION_ACTION_TYPE.initializeVerification),
			map((x: ActionWithPayload<VerificationRequest>) => {
				const { token, personalNumber, transactionId } = x.payload;
				let normalizedPersonalNumber;

				if (personalNumber) {
					normalizedPersonalNumber = normalizePersonalNumber(personalNumber);
				}

				return {
					token,
					personalNumber: normalizedPersonalNumber,
					transactionId,
				};
			}),
			switchMap((request) =>
				this.service.initializeVerification(request).pipe(
					map((response) =>
						this.action.initializeVerificationSuccess({
							token: request.token,
							personalNumber: request.personalNumber,
							autostartToken: response.autostartToken,
							transactionId: response.transactionId,
						})
					),
					catchError((error: AppError) =>
						error.code === AUTH_TRANSACTION_CODES.validationError &&
						error.data.errors &&
						error.data.errors.length
							? of(this.action.initializeVerificationFail(new Error(error.data.errors[0].code)))
							: of(this.initializeFail(AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail, error))
					)
				)
			)
		)
	);

	initializePolling$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationSuccess,
				AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionSuccess
			),
			map((x: ActionWithPayload<InitializeSuccessPayload>) => x.payload),
			map((data) =>
				batchActions(
					this.action.getVerificationStatus({
						token: data.token,
						personalNumber: data.personalNumber,
						transactionId: data.transactionId,
						previousTransactionId: data.previousTransactionId,
						authenticationType: data.authenticationType,
						lastStatus: !data.startBankIdTimings,
					}),
					this.sessionAction.updateCustomerNotVerifiedData(data)
				)
			)
		)
	);

	getVerificationStatus$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus),
				map((x: ActionWithPayload<VerificationStatusData>) => x.payload),
				filter((x: VerificationStatusData) => !x.lastStatus),
				switchMap((data) => {
					const transactionTimeout$ = this.timingsService.countdown$.pipe(
						filter((value) => value === 0),
						take(1),
						tap(() => {
							this.timingsService.countdownInterval$.next(undefined);
						}),
						map(() => ({
							status: VerificationStatus.Failed,
							hintCode: HintCode.StartFailed,
							transactionTimeout: true,
						})),
						share()
					);

					const qrPackTimeout$ = interval(this.config.getQrPackTimeout()).pipe(
						take(1),
						map(() => ({
							status: VerificationStatus.Failed,
							hintCode: HintCode.StartFailed,
							transactionTimeout: false,
						})),
						share()
					);

					const polling$ = interval(this.config.getVerificationStatusInterval()).pipe(
						exhaustMap(() =>
							this.service.getStatus({
								token: data.token,
								personalNumber: data.personalNumber,
								transactionId: data.transactionId,
								authenticationType: data.authenticationType,
							})
						),
						timeoutWith(
							this.config.getVerificationStatusTimeout(),
							of({
								status: VerificationStatus.Failed,
								hintCode: HintCode.Timeout,
							})
						),
						retryWhen((errors) =>
							errors.pipe(
								mergeMap((error, index) => {
									// IOS bug in WKWebView, which causes an xhr error
									if (error.data && error.data.isTrusted && index === 0) {
										return timer(0);
									}
									throw error;
								})
							)
						),
						takeUntil(transactionTimeout$)
					);

					return merge(polling$, qrPackTimeout$, transactionTimeout$).pipe(
						takeWhile((response) => response.status === VerificationStatus.Pending, true),
						map((response) => this.mapVerificationResponse(response)),
						catchError((error: AppError) =>
							error.code === AUTH_TRANSACTION_CODES.validationError &&
							error.data.errors &&
							error.data.errors.length
								? of(this.action.getVerificationStatusFail(new Error(error.data.errors[0].code)))
								: of(this.initializeFail(AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail, error))
						)
					);
				})
			)
		)
	);

	getLastVerificationStatus$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus),
				map((x: ActionWithPayload<VerificationStatusData>) => x.payload),
				filter((x: VerificationStatusData) => !!x.lastStatus),
				switchMap((data) => {
					const polling$ = this.service
						.getStatus({
							token: data.token,
							personalNumber: data.personalNumber,
							transactionId: data.previousTransactionId,
							authenticationType: data.authenticationType,
						})
						.pipe(
							timeoutWith(
								this.config.getVerificationStatusTimeout(),
								of({
									status: VerificationStatus.Failed,
									hintCode: HintCode.Timeout,
								})
							),
							retryWhen((errors) =>
								errors.pipe(
									mergeMap((error, index) => {
										// IOS bug in WKWebView, which causes an xhr error
										if (error.data && error.data.isTrusted && index === 0) {
											return timer(0);
										}
										throw error;
									})
								)
							)
						);

					return polling$.pipe(
						map((response) => {
							if (response.status === VerificationStatus.Completed) {
								return this.action.getVerificationStatusSuccess(response);
							} else if (response.status === VerificationStatus.Pending) {
								return batchActions(
									this.action.cancelVerification({
										transactionTimeout: false,
										customerNotVerifiedData: {
											token: data.token,
											personalNumber: data.personalNumber,
											transactionId: data.previousTransactionId,
											authenticationType: data.authenticationType,
										},
									}),
									this.action.getVerificationStatus({ ...data, lastStatus: false })
								);
							}
							return this.action.getVerificationStatus({ ...data, lastStatus: false });
						}),
						catchError((error: AppError) =>
							error.code === AUTH_TRANSACTION_CODES.validationError &&
							error.data.errors &&
							error.data.errors.length
								? of(this.action.getVerificationStatusFail(new Error(error.data.errors[0].code)))
								: of(this.initializeFail(AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail, error))
						)
					);
				})
			)
		)
	);

	cancelLastVerification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerification),
			map((x: ActionWithPayload<VerificationCancelRequest>) => x.payload),
			map((x) => x?.authenticationType),
			concatLatestFrom(() => this.store.select(this.sessionSelector.getCustomerNotVerifiedData())),
			map(([authenticationType, data]) =>
				data?.transactionId
					? this.action.cancelVerification({
							transactionTimeout: true,
							customerNotVerifiedData: {
								...data,
								...{ token: data.token || data.autostartToken },
								authenticationType,
							},
						})
					: this.action.cancelLastVerificationFail(new Error("Data Transaction ID is not set"))
			)
		)
	);

	cancelVerification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_TRANSACTION_ACTION_TYPE.cancelVerification),
			map((x: ActionWithPayload<CancelVerificationData>) => x.payload),
			switchMap((data) =>
				this.service
					.cancelVerification({
						token: data.customerNotVerifiedData.token,
						transactionId: data.customerNotVerifiedData.transactionId!,
						authenticationType: data.customerNotVerifiedData.authenticationType,
					})
					.pipe(
						map(() => this.action.cancelLastVerificationSuccess(data)),
						catchError((error: AppError) => of(this.action.cancelLastVerificationFail(error)))
					)
			)
		)
	);

	resetVerification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.login,
				SESSION_ACTION_TYPE.logout,
				SESSION_ACTION_TYPE.loginWithPersonalNumber,
				SESSION_ACTION_TYPE.loginWithVerificationCode
			),
			map(() => this.action.reset())
		)
	);

	clearOnLoginWithTokenSuccessRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithTokenSuccess),
			/* TODO: separate SGA to a different effect file, and don't remove the authentication token at all.
			For now, delay the removal of the token. Agreed with Techsson to keep it up to 30s */
			delay(10000),
			map(() => this.action.reset())
		)
	);

	clearVerificationStatus$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithToken),
			map(() => this.action.resetVerificationStatus())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail]:
					AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				[AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail]:
					AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeVerification,
				[AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail]:
					AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction,
				[AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerificationFail]:
					AUTH_TRANSACTION_MESSAGE_CONTEXT.cancelLastVerification,
			}),
			this.messageService.mapClearByContext$({
				[AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus]:
					AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				[AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction]:
					AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction,
				[AUTH_TRANSACTION_ACTION_TYPE.initializeVerification]:
					AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeVerification,
				[AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerification]:
					AUTH_TRANSACTION_MESSAGE_CONTEXT.cancelLastVerification,
			}),
			this.messageService.map$({
				[AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusSuccess]: {
					context: AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_TRANSACTION_CODES.verificationStatus.completed,
				},
				[AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionSuccess]: {
					context: AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_TRANSACTION_CODES.initializeAuthTransaction.success,
				},
				[AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerificationSuccess]: {
					context: AUTH_TRANSACTION_ACTION_TYPE.cancelLastVerification,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_TRANSACTION_CODES.cancelLastVerification.success,
				},
				[AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus]: {
					context: AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus,
					scope: MessageScopeType.Local,
					type: MessageType.Info,
					messageCode: AUTH_TRANSACTION_CODES.verificationStatus.start,
				},
			})
		)
	);

	private logger: Log;

	constructor(
		loggingFactory: LoggingFactory,
		private actions$: Actions,
		private action: AuthTransactionAction,
		private service: AuthTransactionService,
		private timingsService: AuthTransactionTimingsService,
		private config: AuthTransactionConfigService,
		private platformService: PlatformService,
		private messageService: MessageService,
		private sessionAction: SessionAction,
		private sessionSelector: SessionSelector,
		private authTransactionSelector: AuthTransactionSelector,
		private store: Store<CoreState>
	) {
		this.logger = loggingFactory.get("AuthTransactionEffect");
	}

	private initializeFail(actionType: string, error: AppError | Error): ActionWithPayload {
		switch (actionType) {
			case AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail:
				this.logger.warn("initializeAuthTransaction$", JSON.stringify(error));
				return this.action.initializeAuthTransactionFail(error);

			case AUTH_TRANSACTION_ACTION_TYPE.initializeVerificationFail:
				this.logger.warn("initializeVerification$", JSON.stringify(error));
				return this.action.initializeVerificationFail(error);

			default:
				this.logger.warn("getVerificationStatus$", JSON.stringify(error));
				return this.action.getVerificationStatusFail(error);
		}
	}

	private mapVerificationFailedResponse(response: VerificationStatusResponse): ActionWithPayload<any> {
		let error = AUTH_TRANSACTION_CODES.verificationStatus.failed.generic;

		switch (response.hintCode) {
			case HintCode.Timeout:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.timeout;
				break;
			case HintCode.AlreadyInProgress:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.alreadyInProgress;
				break;
			case HintCode.Cancelled:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.cancelled;
				break;
			case HintCode.CertificateError:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.certificateError;
				break;
			case HintCode.ExpiredTransaction:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.expiredTransaction;
				break;
			case HintCode.ProviderError:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.providerError;
				break;
			case HintCode.StartFailed:
				if (!response.transactionTimeout) {
					return this.action.initializeAuthTransaction({
						authenticationType: AuthenticationType.BankId,
						shouldRememberUser: true,
					});
				}
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.startFailed;
				break;
			case HintCode.Unknown:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.unknown;
				break;
			case HintCode.UserCancel:
				error = AUTH_TRANSACTION_CODES.verificationStatus.failed.userCancel;
		}

		return this.action.getVerificationStatusFail(new Error(error));
	}

	private mapVerificationResponse(response: VerificationStatusResponse): ActionWithPayload {
		switch (response.status) {
			case VerificationStatus.Completed:
				return this.action.getVerificationStatusSuccess(response);

			case VerificationStatus.Failed:
				return this.mapVerificationFailedResponse(response);

			default:
				return this.action.getVerificationStatusPending(response);
		}
	}
}
